import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { getStripeEnvironment } from '@/lib/stripe';
import StripeEmbeddedCheckoutMount from './StripeEmbeddedCheckout';
import { toast } from '@/hooks/use-toast';

type Body =
  | { kind: 'base'; tier: 'standard' | 'professional'; interval: 'month' | 'year' }
  | { kind: 'module'; moduleKey: string; interval: 'month' | 'year' };

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  request: Body | null;
  title?: string;
  description?: string;
}

const CheckoutDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  request,
  title = 'Complete checkout',
  description,
}) => {
  // Stable callback per-open so the EmbeddedCheckoutProvider doesn't remount
  const [secretFetcher, setSecretFetcher] = useState<
    (() => Promise<string>) | null
  >(null);

  React.useEffect(() => {
    if (!open || !request) {
      setSecretFetcher(null);
      return;
    }
    const fn = async () => {
      const env = getStripeEnvironment();
      const returnUrl = `${window.location.origin}/?billing=success&session_id={CHECKOUT_SESSION_ID}`;
      if (request.kind === 'base') {
        const { data, error } = await supabase.functions.invoke(
          'create-base-checkout',
          {
            body: {
              tier: request.tier,
              interval: request.interval,
              returnUrl,
              environment: env,
            },
          },
        );
        if (error || !(data as any)?.clientSecret) {
          const msg =
            (data as any)?.error || error?.message || 'Could not start checkout';
          toast({ title: 'Checkout error', description: msg, variant: 'destructive' });
          onOpenChange(false);
          throw new Error(msg);
        }
        return (data as any).clientSecret as string;
      }
      const { data, error } = await supabase.functions.invoke(
        'create-module-checkout',
        {
          body: {
            moduleKey: request.moduleKey,
            interval: request.interval,
            returnUrl,
            environment: env,
          },
        },
      );
      if (error || !(data as any)?.clientSecret) {
        const msg =
          (data as any)?.error || error?.message || 'Could not start checkout';
        toast({ title: 'Checkout error', description: msg, variant: 'destructive' });
        onOpenChange(false);
        throw new Error(msg);
      }
      return (data as any).clientSecret as string;
    };
    // Wrap in setState so identity is stable for one dialog open lifecycle
    setSecretFetcher(() => fn);
  }, [open, request, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {secretFetcher && (
          <StripeEmbeddedCheckoutMount fetchClientSecret={secretFetcher} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutDialog;
